import { useState } from 'react';

import { ApiError } from '@solar/api-client';
import { ErrorBox, ROLE_LABEL, Spinner, formatDateTime, useAsync } from '@solar/ui';

import { api } from '../api';
import { Modal } from '../Modal';

/**
 * 계정 관리 > 비밀번호 변경 조회 — 레거시 WebService/auth/user/pw_change_users.blade.php 파리티.
 *
 * 사용자 1행 + 마지막 변경일. 체크한 계정에 대해 비밀번호 일괄변경 / 사용자 일괄삭제 /
 * 사용자 일괄정지. 일괄 처리는 서버가 에너지공사(1,2)만 허용하고 본인은 제외한다 —
 * 여기서는 403/422 메시지를 그대로 보여준다. 레거시는 전체를 한 번에 내렸지만
 * 신규는 페이징한다.
 */

const SIZE = 30;

type Action = 'password' | 'delete' | 'stop';

const ACTION_LABEL: Record<Action, string> = {
  password: '비밀번호 일괄변경',
  delete: '사용자 일괄삭제',
  stop: '사용자 일괄정지',
};

function StatusBadge({ status }: { status: string | null }) {
  if (status === 'stop') return <span className="badge stop">정지</span>;
  return <span className="badge">정상</span>;
}

export function PwChangeUsers() {
  const [page, setPage] = useState(1);
  const [checked, setChecked] = useState<ReadonlySet<number>>(new Set());
  const [action, setAction] = useState<Action | null>(null);
  const [okMessage, setOkMessage] = useState<string | null>(null);

  const list = useAsync(() => api.listPwChangeUsers({ page, size: SIZE }), [page]);

  const total = list.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / SIZE));
  const pageIds = list.data?.items.map((u) => u.id) ?? [];
  const allChecked = pageIds.length > 0 && pageIds.every((id) => checked.has(id));

  function toggle(id: number) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function togglePage(on: boolean) {
    setChecked((prev) => {
      const next = new Set(prev);
      for (const id of pageIds) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  const ids = [...checked];

  return (
    <div className="admin__body">
      <section className="admin__list">
        <div className="batchbar">
          <span className="batchbar__count">{ids.length}건 선택</span>
          {(Object.keys(ACTION_LABEL) as Action[]).map((a) => (
            <button
              key={a}
              type="button"
              disabled={ids.length === 0}
              onClick={() => {
                setOkMessage(null);
                setAction(a);
              }}
            >
              {ACTION_LABEL[a]}
            </button>
          ))}
        </div>
        {okMessage !== null && <p className="ok">{okMessage}</p>}

        {list.loading && <Spinner />}
        {list.error && <ErrorBox message={list.error} onRetry={list.reload} />}

        {list.data && (
          <table className="grid">
            <thead>
              <tr>
                <th className="check-col">
                  <input
                    type="checkbox"
                    aria-label="현재 페이지 전체 선택"
                    checked={allChecked}
                    onChange={(e) => togglePage(e.target.checked)}
                  />
                </th>
                <th>No</th>
                <th>이름</th>
                <th>아이디</th>
                <th>업체명</th>
                <th>권한</th>
                <th>상태</th>
                <th>변경일</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.length === 0 && (
                <tr>
                  <td colSpan={8} className="muted">
                    사용자가 없습니다.
                  </td>
                </tr>
              )}
              {list.data.items.map((u) => (
                <tr key={u.id}>
                  <td className="check-col">
                    <input
                      type="checkbox"
                      aria-label={`${u.user_id} 선택`}
                      checked={checked.has(u.id)}
                      onChange={() => toggle(u.id)}
                    />
                  </td>
                  <td>{u.id}</td>
                  <td>{u.name ?? '-'}</td>
                  <td>{u.user_id}</td>
                  <td>{u.company_name ?? '-'}</td>
                  <td>{ROLE_LABEL[u.type] ?? u.type}</td>
                  <td>
                    <StatusBadge status={u.status} />
                  </td>
                  <td>{u.changed_at ? formatDateTime(u.changed_at) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="pager">
          <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
            이전
          </button>
          <span>
            {page} / {lastPage} (총 {total}건)
          </span>
          <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= lastPage}>
            다음
          </button>
        </div>
      </section>

      {action !== null && (
        <BulkModal
          action={action}
          ids={ids}
          onClose={() => setAction(null)}
          onDone={(message) => {
            setAction(null);
            setChecked(new Set());
            setOkMessage(message);
            list.reload();
          }}
        />
      )}
    </div>
  );
}

/** 일괄 처리 확인 모달 — 비밀번호 변경만 입력이 필요하고, 삭제/정지는 확인만 받는다. */
function BulkModal({
  action,
  ids,
  onClose,
  onDone,
}: {
  action: Action;
  ids: number[];
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    if (action === 'password' && password !== passwordConfirm) {
      setError('비밀번호와 비밀번호 확인이 일치하지 않습니다.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      let count: number;
      if (action === 'password') count = (await api.bulkChangePassword(ids, password)).count;
      else if (action === 'delete') count = (await api.bulkDeleteUsers(ids)).count;
      else count = (await api.bulkStopUsers(ids)).count;
      onDone(`${ACTION_LABEL[action]} 완료 (${count}건)`);
    } catch (err) {
      // 직전 비밀번호 재사용(422), 본인/권한 밖 계정(403) — 서버 메시지 그대로.
      setError(err instanceof ApiError ? err.message : '처리하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={ACTION_LABEL[action]} onClose={onClose}>
      <p>선택한 {ids.length}개 계정에 적용합니다. 한 건이라도 실패하면 전체가 취소됩니다.</p>
      {action === 'password' && (
        <>
          <label className="labeled">
            새 비밀번호 *
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="9~18자 영문+숫자+특수문자(!@#$%^*+=-)"
            />
          </label>
          <label className="labeled">
            비밀번호 확인 *
            <input
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
            />
          </label>
        </>
      )}
      {action === 'delete' && <p className="error">삭제된 계정은 더 이상 로그인할 수 없습니다.</p>}

      {error && <p className="error">{error}</p>}
      <div className="row">
        <button type="button" onClick={onClose}>
          취소
        </button>
        <button
          type="button"
          className="primary"
          disabled={busy || (action === 'password' && password.length === 0)}
          onClick={() => void run()}
        >
          {busy ? '처리 중…' : '확인'}
        </button>
      </div>
    </Modal>
  );
}
